import React, { useEffect, useState } from "react";
import { api, formatDetail, fmtDate } from "@/lib/api";
import { PageHeader, PrimaryButton, SecondaryButton, StatusBadge, EmptyState } from "@/components/ui-parts";
import { Car, Plus, Pencil, Trash2, X, Gauge, Wrench } from "lucide-react";
import { toast } from "sonner";

function VehicleForm({ initial, members, onSave, onCancel }) {
  const [form, setForm] = useState({
    license_plate: initial?.license_plate || "",
    name: initial?.name || "",
    brand: initial?.brand || "",
    model: initial?.model || "",
    tuv_date: initial?.tuv_date || "",
    service_km_interval: initial?.service_km_interval || "",
    current_km: initial?.current_km || "",
    last_service_km: initial?.last_service_km || "",
    assigned_member_id: initial?.assigned_member_id || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    try {
      await onSave({
        ...form,
        service_km_interval: numOrNull(form.service_km_interval),
        current_km: numOrNull(form.current_km),
        last_service_km: numOrNull(form.last_service_km),
        assigned_member_id: form.assigned_member_id || null,
        tuv_date: form.tuv_date || null,
      });
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[#E6E6E4] rounded-md p-6 space-y-4" data-testid="vehicle-form">
      <div className="flex items-center justify-between">
        <div className="font-display font-bold text-lg text-[#1A1A1A]">
          {initial ? `Fahrzeug ${initial.license_plate} bearbeiten` : "Neues Fahrzeug"}
        </div>
        <button type="button" onClick={onCancel} className="text-[#5C5E60] hover:text-[#1A1A1A]">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Kennzeichen *</label>
          <input
            required data-testid="vehicle-plate"
            value={form.license_plate}
            onChange={(e) => setForm({ ...form, license_plate: e.target.value.toUpperCase() })}
            placeholder="B-AB 1234"
            className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm font-mono focus:outline-none focus:border-[#18322B]" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Bezeichnung</label>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Firmen-Transporter"
            className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Marke</label>
          <input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })}
            className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Modell</label>
          <input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })}
            className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">TÜV-Frist</label>
          <input type="date" data-testid="vehicle-tuv"
            value={form.tuv_date} onChange={(e) => setForm({ ...form, tuv_date: e.target.value })}
            className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B]" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Zugewiesener Mitarbeiter</label>
          <select
            value={form.assigned_member_id}
            onChange={(e) => setForm({ ...form, assigned_member_id: e.target.value })}
            className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm bg-white">
            <option value="">— nicht zugewiesen —</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>{m.name || m.email}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Aktueller km-Stand</label>
          <input type="number" data-testid="vehicle-current-km"
            value={form.current_km}
            onChange={(e) => setForm({ ...form, current_km: e.target.value })}
            className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm font-mono focus:outline-none focus:border-[#18322B]" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Letzter Service bei (km)</label>
          <input type="number"
            value={form.last_service_km}
            onChange={(e) => setForm({ ...form, last_service_km: e.target.value })}
            className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm font-mono focus:outline-none focus:border-[#18322B]" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Service-Intervall (km)</label>
          <input type="number" data-testid="vehicle-interval"
            value={form.service_km_interval}
            onChange={(e) => setForm({ ...form, service_km_interval: e.target.value })}
            placeholder="z.B. 30000"
            className="w-full px-3 py-2 border border-[#E6E6E4] rounded-md text-sm font-mono focus:outline-none focus:border-[#18322B]" />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <SecondaryButton type="button" onClick={onCancel}>Abbrechen</SecondaryButton>
        <PrimaryButton type="submit" disabled={saving} data-testid="vehicle-submit">
          {saving ? "Speichern…" : "Speichern"}
        </PrimaryButton>
      </div>
    </form>
  );
}

export default function Vehicles() {
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: v }, { data: t }] = await Promise.all([api.get("/vehicles"), api.get("/team")]);
      setItems(v);
      setMembers(t.filter((m) => !m.is_owner && m.status === "active"));
    } catch { toast.error("Fahrzeuge konnten nicht geladen werden"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    if (editing) {
      await api.patch(`/vehicles/${editing.id}`, payload);
      toast.success("Fahrzeug aktualisiert");
    } else {
      await api.post("/vehicles", payload);
      toast.success("Fahrzeug angelegt");
    }
    setShowForm(false);
    setEditing(null);
    load();
  };

  const remove = async (v) => {
    if (!window.confirm(`Fahrzeug ${v.license_plate} löschen?`)) return;
    try {
      await api.delete(`/vehicles/${v.id}`);
      toast.success("Gelöscht");
      load();
    } catch { toast.error("Fehlgeschlagen"); }
  };

  const daysToTuv = (iso) => {
    if (!iso) return null;
    return Math.round((new Date(iso) - new Date()) / (1000 * 60 * 60 * 24));
  };

  return (
    <div>
      <PageHeader
        title="Fahrzeuge"
        subtitle="Fahrzeuge werden vom Bot beim ersten Tankbeleg automatisch erkannt. Ergänzen Sie TÜV & Service-Intervall für Erinnerungen."
        actions={
          !showForm && (
            <PrimaryButton onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-vehicle-btn">
              <Plus className="h-4 w-4" /> Fahrzeug hinzufügen
            </PrimaryButton>
          )
        }
      />

      <div className="px-8 py-8 space-y-6">
        {showForm && (
          <VehicleForm initial={editing} members={members} onSave={save}
            onCancel={() => { setShowForm(false); setEditing(null); }} />
        )}

        {loading ? (
          <div className="text-sm text-[#5C5E60]">Lade…</div>
        ) : items.length === 0 && !showForm ? (
          <EmptyState
            title="Noch keine Fahrzeuge"
            hint="Sobald ein Mitarbeiter einen Tankbeleg per Telegram sendet, legt der Bot das Fahrzeug automatisch an."
            action={<PrimaryButton onClick={() => setShowForm(true)}><Plus className="h-4 w-4" /> Manuell anlegen</PrimaryButton>}
            testid="vehicles-empty"
          />
        ) : (
          <div className="bg-white border border-[#E6E6E4] rounded-md overflow-hidden">
            <table className="w-full text-sm" data-testid="vehicles-list">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] bg-[#F9F9F8] border-b border-[#E6E6E4]">
                  <th className="text-left px-6 py-3 font-semibold">Kennzeichen</th>
                  <th className="text-left px-4 py-3 font-semibold">Fahrzeug</th>
                  <th className="text-left px-4 py-3 font-semibold">Zugewiesen</th>
                  <th className="text-left px-4 py-3 font-semibold">km-Stand</th>
                  <th className="text-left px-4 py-3 font-semibold">Service</th>
                  <th className="text-left px-4 py-3 font-semibold">TÜV</th>
                  <th className="text-right px-6 py-3 font-semibold">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {items.map((v) => {
                  const memberName = members.find((m) => m.id === v.assigned_member_id)?.name
                    || members.find((m) => m.id === v.assigned_member_id)?.email;
                  const dueKm = v.service_due_in_km;
                  const tuvDays = daysToTuv(v.tuv_date);
                  return (
                    <tr key={v.id} className="border-b border-[#E6E6E4] last:border-b-0 hover:bg-[#F9F9F8]">
                      <td className="px-6 py-3 font-mono font-bold text-[#1A1A1A]">
                        <div className="flex items-center gap-2">
                          <Car className="h-4 w-4 text-[#5C5E60]" strokeWidth={1.75} />
                          {v.license_plate}
                          {v.auto_created && <span className="text-[9px] uppercase tracking-wider bg-[#EAF4FA] text-[#1A4971] px-1.5 py-0.5 rounded">Auto</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-[#1A1A1A]">{v.name || "—"}</div>
                        <div className="text-xs text-[#5C5E60]">{[v.brand, v.model].filter(Boolean).join(" ") || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-[#5C5E60]">{memberName || "—"}</td>
                      <td className="px-4 py-3 font-mono-tab text-[#1A1A1A]">
                        <div className="flex items-center gap-1">
                          <Gauge className="h-3.5 w-3.5 text-[#5C5E60]" strokeWidth={1.75} />
                          {v.current_km ? `${v.current_km.toLocaleString("de-DE")} km` : "—"}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {v.service_km_interval ? (
                          <div className="flex items-center gap-1 text-xs">
                            <Wrench className="h-3.5 w-3.5 text-[#5C5E60]" strokeWidth={1.75} />
                            {dueKm !== null && dueKm <= 500 ? (
                              <StatusBadge variant="warning">in {Math.max(dueKm, 0)} km</StatusBadge>
                            ) : (
                              <span className="text-[#5C5E60]">in {dueKm?.toLocaleString("de-DE")} km</span>
                            )}
                          </div>
                        ) : <span className="text-[#5C5E60] text-xs">Kein Intervall</span>}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {v.tuv_date ? (
                          tuvDays !== null && tuvDays <= 45 ? (
                            <StatusBadge variant={tuvDays < 0 ? "error" : "warning"}>
                              {tuvDays < 0 ? "abgelaufen" : `in ${tuvDays} T.`}
                            </StatusBadge>
                          ) : (
                            <span className="text-[#5C5E60]">{fmtDate(v.tuv_date)}</span>
                          )
                        ) : (
                          <span className="text-[#5C5E60]">—</span>
                        )}
                      </td>
                      <td className="text-right px-6 py-3">
                        <button onClick={() => { setEditing(v); setShowForm(true); }}
                          data-testid={`edit-vehicle-${v.id}`}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md hover:bg-[#F3F3F1] text-[#1A1A1A] mr-1">
                          <Pencil className="h-3.5 w-3.5" /> Bearbeiten
                        </button>
                        <button onClick={() => remove(v)}
                          data-testid={`delete-vehicle-${v.id}`}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md hover:bg-[#FCE8E8] text-[#B31D1D]">
                          <Trash2 className="h-3.5 w-3.5" /> Löschen
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
